// SDK › Signals (D111, APS-358): what each app's devices send, per signal family, and what they
// keep, as the policy's instrumentation object holds it (spec 15). The page edits a draft of it;
// these are the draft's rules, kept out of the page so they are tested on their own. The server
// validates the same object against the policy schema (packages/edge-config). D114 (APS-428): a
// family's triggers say what they release (`after`), and a family can keep its records for its
// own time and within its own share of the store.
import type {
  DeviceRequest,
  AppInstrumentation as Instrumentation,
  InstrumentationFamily,
  InstrumentationTrigger,
  Platform,
  InstrumentationRule as Rule,
  SignalFamily,
} from '../api/types'
import type { Translator } from '../i18n'

/** The families in the mock's order: the summary first, resources last (D113). */
export const FAMILIES = [
  'summary',
  'errors',
  'logs',
  'spans',
  'network',
  'metrics',
  'replay',
  'resources',
] as const satisfies readonly SignalFamily[]

/**
 * The first release of each platform's SDK that records the resources family (spec 7.7, 7.9);
 * null while it does not. APS-462: Android (APS-498) and iOS (APS-497) from 0.2.0, React Native
 * (APS-499) from its first release, 0.1.0: the versions tagged at the beta freeze (APS-464).
 */
export const RESOURCES_SINCE: Record<Platform, string | null> = {
  android: '0.2.0',
  ios: '0.2.0',
  react_native: '0.1.0',
}

/** Whether an app's platform SDK records the resources family yet, and from which release. */
export function resourcesSdk(
  t: Translator['t'],
  platform: Platform,
  label: string,
): { since: string | null; line: string } {
  const since = RESOURCES_SINCE[platform]
  return {
    since,
    line:
      since === null
        ? t('signals.resourcesNotYet', { platform: label })
        : t('signals.resourcesSince', { platform: label, version: since }),
  }
}

/**
 * A family's mode when the policy leaves it out (spec 15.3): always, except resources, which a
 * backend turns on (never).
 */
export const defaultMode = (family: SignalFamily): 'always' | 'never' =>
  family === 'resources' ? 'never' : 'always'

/** The modes in the mock's order, from nothing to everything. */
export const MODES = ['never', 'on_trigger', 'sampled', 'always'] as const
export type Mode = (typeof MODES)[number]

/** The sample rates the stepper walks, in percent (the mock's steps). */
export const RATE_STEPS = [1, 5, 10, 25, 50, 100] as const

/** The built-in triggers (spec 15.4, rage and dead taps 8.7); a named app event is `event:<name>`. */
export const BUILTIN_TRIGGERS = ['crash', 'error', 'anr', 'rage_tap', 'dead_tap'] as const

/** An app event's name as the policy schema allows it. */
export const EVENT_NAME = /^[A-Za-z0-9._-]{1,64}$/

/** D111's limits: retention 1 h to 7 days, the store's cap 20 MiB, replay's share 10 MiB. */
export const LIMITS = {
  retentionHours: { min: 1, max: 168, default: 24 },
  capMb: { min: 1, max: 20, default: 20 },
  replayCapMb: { min: 1, max: 10, default: 10 },
  windowMinutes: { min: 1, max: 60, default: 5 },
} as const

const MB = 1024 * 1024

/** What a trigger releases after its window (spec 15.4): the rest of the session, or nothing. */
export type After = 'session' | 'window'

/** A trigger's own window and `after`, as a rule's page sets them for its `rule:<id>`. */
export interface TriggerOverride {
  windowMinutes?: number
  after?: After
}

/**
 * One family as the page edits it: every field present, the rate in percent. `after` applies
 * to the triggers the row picks; a trigger in `overrides` keeps its own (a rule's window and
 * `after`, a built-in's window). A family's retention or cap left null is the store's.
 */
export interface FamilyDraft {
  mode: Mode
  ratePct: number
  triggers: string[]
  windowMinutes: number
  after: After
  overrides: Record<string, TriggerOverride>
  retentionHours: number | null
  capMb: number | null
}

export interface Draft {
  families: Record<SignalFamily, FamilyDraft>
  retentionHours: number
  capMb: number
  replayCapMb: number
  /** Edited on each rule's page; the family page sends them back as they are. */
  rules: Rule[]
  /** The updated_at it was read at: a publish against an older one is refused (409). */
  updatedAt: string | null
}

/** A rule's trigger name, or undefined for any other trigger. */
export const ruleId = (trigger: string): string | undefined =>
  trigger.startsWith('rule:') ? trigger.slice('rule:'.length) : undefined

/** The nearest step to a rate (0 to 1), so a rate set elsewhere still shows on the stepper. */
export function nearestStep(rate: number): number {
  const pct = rate * 100
  return RATE_STEPS.reduce((a, b) => (Math.abs(b - pct) < Math.abs(a - pct) ? b : a))
}

const entryOf = (t: InstrumentationTrigger) => (typeof t === 'string' ? { trigger: t } : t)

function familyDraft(name: SignalFamily, f: InstrumentationFamily | undefined): FamilyDraft {
  const entries = (f?.triggers ?? []).map(entryOf)
  const overrides: Record<string, TriggerOverride> = {}
  // The row's `after` is the one its own triggers share; a rule keeps its own on its page.
  let after: After = 'session'
  for (const e of entries) {
    const own = {
      ...(e.window_minutes !== undefined ? { windowMinutes: e.window_minutes } : {}),
      ...(e.after !== undefined ? { after: e.after } : {}),
    }
    if (ruleId(e.trigger) !== undefined) {
      if (Object.keys(own).length > 0) overrides[e.trigger] = own
    } else {
      if (e.after === 'window') after = 'window'
      // The page does not edit a built-in's own window, but a save keeps it.
      if (e.window_minutes !== undefined) overrides[e.trigger] = { windowMinutes: e.window_minutes }
    }
  }
  return {
    mode: f?.mode ?? defaultMode(name),
    ratePct: f?.rate === undefined ? 10 : Math.round(f.rate * 1000) / 10,
    triggers: entries.map((e) => e.trigger),
    windowMinutes: f?.window_minutes ?? LIMITS.windowMinutes.default,
    after,
    overrides,
    retentionHours: f?.retention_hours ?? null,
    capMb: f?.cap_bytes === undefined ? null : Math.round(f.cap_bytes / MB),
  }
}

/** A trigger picked or dropped: a dropped trigger loses its own window and `after` too. */
export function toggleTrigger(f: FamilyDraft, trigger: string): Partial<FamilyDraft> {
  if (!f.triggers.includes(trigger)) return { triggers: [...f.triggers, trigger] }
  const { [trigger]: _, ...overrides } = f.overrides
  return { triggers: f.triggers.filter((x) => x !== trigger), overrides }
}

/** The page's draft of an app's instrumentation: a family left out is in always mode. */
export function toDraft(x: Instrumentation): Draft {
  return {
    families: Object.fromEntries(
      FAMILIES.map((f) => [f, familyDraft(f, x.families[f])]),
    ) as Draft['families'],
    retentionHours: x.retention_hours,
    capMb: Math.round(x.cap_bytes / MB),
    replayCapMb: Math.round(x.replay_cap_bytes / MB),
    rules: x.rules ?? [],
    updatedAt: x.updated_at,
  }
}

/** A trigger as the policy lists it: a name, or an object when it releases otherwise. */
function triggerEntry(f: FamilyDraft, trigger: string): InstrumentationTrigger {
  const own = f.overrides[trigger]
  const after = own?.after ?? (ruleId(trigger) === undefined ? f.after : 'session')
  const windowMinutes = own?.windowMinutes
  if (after === 'session' && windowMinutes === undefined) return trigger
  return {
    trigger,
    ...(windowMinutes !== undefined ? { window_minutes: windowMinutes } : {}),
    ...(after === 'window' ? { after } : {}),
  }
}

/**
 * The draft as the API takes it (PUT replaces the families whole). A family in its default mode
 * (always; resources never) is left out, as the defaults leave it; the summary is always left out, since
 * release health is never sampled (D111). Fields a mode does not use are not sent.
 */
export function toBody(d: Draft) {
  const families: Partial<Record<SignalFamily, InstrumentationFamily>> = {}
  for (const name of FAMILIES) {
    if (name === 'summary') continue
    const f = d.families[name]
    const triggers = f.mode === 'on_trigger' || f.mode === 'sampled' ? f.triggers : []
    // A family in its default mode with nothing of its own is left out, as the defaults leave it.
    if (f.mode === defaultMode(name) && f.retentionHours === null && f.capMb === null) continue
    families[name] = {
      mode: f.mode,
      ...(f.mode === 'sampled' ? { rate: f.ratePct / 100 } : {}),
      ...(triggers.length > 0
        ? { triggers: triggers.map((x) => triggerEntry(f, x)), window_minutes: f.windowMinutes }
        : {}),
      ...(f.retentionHours !== null ? { retention_hours: f.retentionHours } : {}),
      ...(f.capMb !== null ? { cap_bytes: f.capMb * MB } : {}),
    }
  }
  return {
    families,
    retention_hours: d.retentionHours,
    cap_bytes: d.capMb * MB,
    replay_cap_bytes: d.replayCapMb * MB,
    // A rule goes back as the API takes it: its author is the server's to keep or set.
    rules: d.rules.map(({ id, on, where, name }) => ({
      id,
      on,
      ...(where?.length ? { where } : {}),
      name,
    })),
    // What it was read at: the API refuses a publish over a change made meanwhile (409).
    updated_at: d.updatedAt,
  }
}

/** Whether two drafts would publish the same policy. */
export const sameDraft = (a: Draft, b: Draft): boolean =>
  JSON.stringify(toBody(a)) === JSON.stringify(toBody(b))

/** What is wrong with a draft, as message ids with their family, before it is sent. */
export function draftErrors(d: Draft): {
  family?: SignalFamily
  field: 'triggers' | 'window' | 'retention' | 'cap' | 'replayCap'
}[] {
  const errors: ReturnType<typeof draftErrors> = []
  for (const name of FAMILIES) {
    const f = d.families[name]
    if (f.mode === 'on_trigger' && f.triggers.length === 0)
      errors.push({ family: name, field: 'triggers' })
    const usesWindow = f.mode === 'on_trigger' || (f.mode === 'sampled' && f.triggers.length > 0)
    if (usesWindow && !inRange(f.windowMinutes, LIMITS.windowMinutes))
      errors.push({ family: name, field: 'window' })
    // A family's own limits: within the profile's, and its cap a share of the store, never more.
    if (f.retentionHours !== null && !inRange(f.retentionHours, LIMITS.retentionHours))
      errors.push({ family: name, field: 'retention' })
    if (f.capMb !== null && (!inRange(f.capMb, LIMITS.capMb) || f.capMb > d.capMb))
      errors.push({ family: name, field: 'cap' })
  }
  if (!inRange(d.retentionHours, LIMITS.retentionHours)) errors.push({ field: 'retention' })
  if (!inRange(d.capMb, LIMITS.capMb)) errors.push({ field: 'cap' })
  if (!inRange(d.replayCapMb, LIMITS.replayCapMb) || d.replayCapMb > d.capMb)
    errors.push({ field: 'replayCap' })
  return errors
}

const inRange = (n: number, r: { min: number; max: number }) =>
  Number.isInteger(n) && n >= r.min && n <= r.max

/** The next or previous step from a rate in percent. */
export function stepRate(pct: number, dir: 1 | -1): number {
  const i = RATE_STEPS.findIndex((s) => s >= pct)
  const at = i < 0 ? RATE_STEPS.length - 1 : RATE_STEPS[i] === pct ? i : dir > 0 ? i - 1 : i
  return RATE_STEPS[Math.min(RATE_STEPS.length - 1, Math.max(0, at + dir))] ?? pct
}

/** A trigger's event name, or undefined for a built-in trigger. */
export const eventName = (trigger: string): string | undefined =>
  trigger.startsWith('event:') ? trigger.slice('event:'.length) : undefined

/** Retention as people say it: "24 h" under two days, "7 days" in whole days. */
export function retentionParts(hours: number): { n: number; unit: 'h' | 'd' } {
  return hours >= 48 && hours % 24 === 0 ? { n: hours / 24, unit: 'd' } : { n: hours, unit: 'h' }
}

/**
 * A request's state on the page: delivered once a device answered it; replaced when a newer
 * capture for the same install or user took its place (APS-454).
 */
export type RequestState = 'open' | 'delivered' | 'expired' | 'canceled' | 'replaced'

export function requestState(r: DeviceRequest): RequestState {
  if (r.answers && r.answers.devices > 0) return 'delivered'
  return r.state
}

/** Open requests a project may have at once (D111 addendum); POST answers 409 beyond. */
export const MAX_OPEN_REQUESTS = 100
